import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Tooltip from '../../../components/molecules/Tooltip';
import ArtifactLoader from './ArtifactLoader';
import { parseArtifactData } from './parseArtifactData';
import css from './MediaCollectionArtifact.css';

const mediaItemShape = PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    thumbnail_url: PropTypes.string.isRequired,
    link_url: PropTypes.string,
});

const MediaThumbnail = ({ item }) => {
    const [failed, setFailed] = useState(false);

    if (failed) {
        return (
            <span className={css.thumbnailFallback}>
                <Translate id="STATIC.PAGES.ANALYSIS_CHAT.PREVIEW_UNAVAILABLE" />
            </span>
        );
    }
    return <img src={item.thumbnail_url} alt={item.title} className={css.thumbnail} onError={() => setFailed(true)} />;
};

MediaThumbnail.propTypes = {
    item: mediaItemShape.isRequired,
};

const MediaCard = ({ item }) => {
    const thumbnail = <MediaThumbnail item={item} />;

    return (
        <div className={css.card}>
            {item.link_url ? (
                <a href={item.link_url} target="_blank" rel="noopener noreferrer" className={css.thumbnailLink}>
                    {thumbnail}
                </a>
            ) : (
                thumbnail
            )}
            {item.title && (
                <Tooltip title={item.title} contentClass={css.tooltipContent}>
                    <span className={css.title} dir="auto">
                        {item.title}
                    </span>
                </Tooltip>
            )}
        </div>
    );
};

MediaCard.propTypes = {
    item: mediaItemShape.isRequired,
};

export const MediaCollectionArtifactInner = ({ data }) => {
    const parsed = useMemo(() => parseArtifactData(data), [data]);

    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const totalItems = Number.isInteger(parsed?.total_items) ? parsed.total_items : items.length;
    const isTruncated = parsed?.truncated === true && totalItems > items.length;
    if (!items.length) return null;

    return (
        <div className={css.mediaCollection}>
            <div className={css.collection}>
                {items.map(item => (
                    <MediaCard key={`${item.id}:${item.thumbnail_url}`} item={item} />
                ))}
            </div>
            {isTruncated && (
                <div className={css.truncationNotice}>
                    <Translate
                        id="STATIC.PAGES.ANALYSIS_CHAT.MEDIA_COLLECTION_TRUNCATED"
                        data={{ shown: items.length, total: totalItems }}
                    />
                </div>
            )}
        </div>
    );
};

MediaCollectionArtifactInner.propTypes = {
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

const MediaCollectionArtifact = ({ artifact }) => (
    <ArtifactLoader artifact={artifact}>{data => <MediaCollectionArtifactInner data={data} />}</ArtifactLoader>
);

MediaCollectionArtifact.propTypes = {
    artifact: PropTypes.shape({
        data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        content_url: PropTypes.string,
    }).isRequired,
};

export default MediaCollectionArtifact;
